import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from '../../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DetailedReport = ({ estimate, user }) => {
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const loadReport = async () => {
    if (report) {
      setIsExpanded(!isExpanded);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/estimates/${estimate.id}/detailed-report`,
        { params: { user_id: user?.id || estimate?.user_id } }
      );
      setReport(res.data);
      setIsExpanded(true);
    } catch (err) {
      console.error('Error loading detailed report:', err);
      const msg = err.response?.data?.detail || 'Failed to load detailed report';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLineItemRow = (item, i) => (
    <tr key={i} className="border-b border-gray-100">
      <td className="py-2 pr-4">{item.description}</td>
      <td className="text-right py-2 px-2">{item.quantity?.toLocaleString()}</td>
      <td className="text-center py-2 px-2 text-gray-500">{item.unit}</td>
      <td className="text-right py-2 px-2">{formatCurrency(item.unit_price)}</td>
      <td className="text-right py-2 pl-2 font-medium">
        {formatCurrency(item.total)}
        {item.extra_cost > 0 && (
          <span className="text-green-600 text-xs ml-1">(+{formatCurrency(item.extra_cost)})</span>
        )}
      </td>
    </tr>
  );

  const tableHeader = (
    <thead>
      <tr className="border-b border-gray-200 text-gray-500">
        <th className="text-left py-2 pr-4">Description</th>
        <th className="text-right py-2 px-2">Qty</th>
        <th className="text-center py-2 px-2">Unit</th>
        <th className="text-right py-2 px-2">Rate</th>
        <th className="text-right py-2 pl-2">Total</th>
      </tr>
    </thead>
  );

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="detailed-report-section">
      <button
        onClick={loadReport}
        className="w-full flex items-center justify-between text-left"
        data-testid="detailed-report-toggle"
      >
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-600" />
          Detailed Report
        </h2>
        <div className="flex items-center gap-2">
          {isLoading && <span className="text-sm text-gray-500">Loading...</span>}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </div>
      </button>

      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}

      {isExpanded && report && (
        <div className="mt-4 space-y-4" data-testid="detailed-report-content">
          {/* Download & Email Actions */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                let text = `DETAILED ESTIMATE REPORT\n${'='.repeat(50)}\n`;
                text += `Address: ${report.address || 'N/A'}\n`;
                text += `Roof Type: ${report.roof_type_display}\n`;
                text += `Area: ${report.square_footage} SF (${report.roof_squares} squares)\n\n`;
                text += `MATERIALS\n${'-'.repeat(30)}\n`;
                (report.material_items || []).forEach(m => {
                  text += `${m.description}: ${m.quantity} ${m.unit} @ ${formatCurrency(m.unit_price)} = ${formatCurrency(m.total)}`;
                  if (m.extra_cost > 0) text += ` (+${formatCurrency(m.extra_cost)})`;
                  text += '\n';
                });
                text += `Materials Subtotal: ${formatCurrency(report.materials_subtotal)}\n\n`;
                text += `LABOR\n${'-'.repeat(30)}\n`;
                (report.labor_items || []).forEach(l => {
                  text += `${l.description}: ${l.quantity} ${l.unit} @ ${formatCurrency(l.unit_price)} = ${formatCurrency(l.total)}`;
                  if (l.extra_cost > 0) text += ` (+${formatCurrency(l.extra_cost)})`;
                  text += '\n';
                });
                text += `Labor Subtotal: ${formatCurrency(report.labor_subtotal)}\n\n`;
                if (report.gutter_guard_items?.length > 0) {
                  text += `GUTTERS / GUARDS / DOWNSPOUTS\n${'-'.repeat(30)}\n`;
                  report.gutter_guard_items.forEach(g => {
                    text += `${g.description}: ${g.quantity} ${g.unit} @ ${formatCurrency(g.unit_price)} = ${formatCurrency(g.total)}\n`;
                  });
                  text += '\n';
                }
                if (report.additional_charges?.length > 0) {
                  text += `ADDITIONAL CHARGES\n${'-'.repeat(30)}\n`;
                  report.additional_charges.forEach(c => text += `${c.label || c.description}: +${formatCurrency(c.amount)}\n`);
                  text += '\n';
                }
                text += `TOTALS\n${'-'.repeat(30)}\n`;
                text += `Subtotal: ${formatCurrency(report.subtotal)}\n`;
                if (report.overhead_profit_percent > 0) text += `O&P (${report.overhead_profit_percent}%): +${formatCurrency(report.overhead_profit_amount)}\n`;
                if (report.sales_tax_rate > 0) text += `Sales Tax (${report.sales_tax_rate}%): +${formatCurrency(report.sales_tax_amount)}\n`;
                if (report.sales_tax_on_additional_charges > 0) text += `Tax on Add'l Charges: +${formatCurrency(report.sales_tax_on_additional_charges)}\n`;
                text += `TOTAL: ${formatCurrency(report.total)}\n`;
                const blob = new Blob([text], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `detailed-estimate-${report.estimate_id || 'report'}.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 border border-indigo-200"
              data-testid="download-detailed-report"
            >
              <Download className="w-4 h-4" /> Download
            </button>
          </div>
          {/* Property Summary */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <p className="text-gray-500">Roof Type</p>
                <p className="font-semibold">{report.roof_type_display}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Area</p>
                <p className="font-semibold">{report.square_footage?.toLocaleString()} SF</p>
              </div>
              <div>
                <p className="text-gray-500">Roof Squares</p>
                <p className="font-semibold">{report.roof_squares}</p>
              </div>
              <div>
                <p className="text-gray-500">Pitch</p>
                <p className="font-semibold">{report.satellite_data?.avg_pitch_degrees || 'N/A'}&deg;</p>
              </div>
            </div>
          </div>

          {/* Materials Section */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Materials
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="materials-table">
                {tableHeader}
                <tbody>
                  {report.material_items?.map(renderLineItemRow)}
                  <tr className="bg-blue-50 font-semibold">
                    <td colSpan={4} className="py-2 pr-4">Materials Subtotal</td>
                    <td className="text-right py-2 pl-2">{formatCurrency(report.materials_subtotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Labor Section */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Labor
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="labor-table">
                {tableHeader}
                <tbody>
                  {report.labor_items?.map(renderLineItemRow)}
                  <tr className="bg-green-50 font-semibold">
                    <td colSpan={4} className="py-2 pr-4">Labor Subtotal</td>
                    <td className="text-right py-2 pl-2">{formatCurrency(report.labor_subtotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Gutters / Guards / Downspouts Section */}
          {report.gutter_guard_items?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                Gutters, Guards & Downspouts
                <span className="text-xs font-normal text-gray-500 ml-1">(Materials + Labor included)</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="gutter-table">
                  {tableHeader}
                  <tbody>
                    {report.gutter_guard_items.map(renderLineItemRow)}
                    <tr className="bg-cyan-50 font-semibold">
                      <td colSpan={4} className="py-2 pr-4">Gutters/Guards/Downspouts Subtotal</td>
                      <td className="text-right py-2 pl-2">
                        {formatCurrency(report.gutter_guard_items.reduce((sum, it) => sum + (it.total || 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="border-t-2 border-gray-300 pt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal (Materials + Labor)</span>
              <span className="font-semibold">{formatCurrency(report.subtotal)}</span>
            </div>
            
            {report.overhead_profit_percent > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Overhead & Profit ({report.overhead_profit_percent}%)</span>
                <span className="font-semibold">+{formatCurrency(report.overhead_profit_amount)}</span>
              </div>
            )}

            {report.stored_pricing && report.overhead_profit_percent > 0 && (
              <div className="bg-gray-50 rounded px-3 py-2 space-y-1 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Materials (incl. O&P)</span>
                  <span>{formatCurrency(report.stored_pricing.materials_with_op)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Labor (incl. O&P)</span>
                  <span>{formatCurrency(report.stored_pricing.labor_with_op)}</span>
                </div>
              </div>
            )}

            {report.steep_roof_applied && report.steep_roof_charge > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Steep Roof Charge</span>
                <span className="font-semibold">+{formatCurrency(report.steep_roof_charge)}</span>
              </div>
            )}

            {report.sales_tax_rate > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Tax ({report.sales_tax_rate}%)</span>
                <span className="font-semibold">+{formatCurrency(report.sales_tax_amount)}</span>
              </div>
            )}

            {/* Additional Charges */}
            {report.additional_charges?.length > 0 && (
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Additional Charges</p>
                {report.additional_charges.map((charge, i) => (
                  <div key={i} className="flex justify-between text-green-700">
                    <span>{charge.label || charge.description}</span>
                    <span className="font-semibold">+{formatCurrency(charge.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {report.sales_tax_on_additional_charges > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Sales Tax on Additional Charges ({report.sales_tax_rate}%)</span>
                <span className="font-semibold">+{formatCurrency(report.sales_tax_on_additional_charges)}</span>
              </div>
            )}

            {report.discount_percent > 0 && (
              <>
                <div className="flex justify-between py-2 border-t border-gray-200 bg-gray-50 px-2 rounded">
                  <span className="text-gray-700 font-medium">Subtotal before discount</span>
                  <span className="font-semibold">{formatCurrency(report.total + report.discount_amount)}</span>
                </div>
                <div className="flex justify-between text-orange-700">
                  <span>Discount ({report.discount_percent}%)</span>
                  <span className="font-semibold">-{formatCurrency(report.discount_amount)}</span>
                </div>
              </>
            )}

            <div className="flex justify-between py-3 bg-indigo-600 text-white px-4 rounded-lg mt-2">
              <span className="text-lg font-bold">Total</span>
              <span className="text-2xl font-bold">{formatCurrency(report.total)}</span>
            </div>
          </div>

          {/* Pricing Unit Info */}
          {report.pricing_units && (report.pricing_units.material === 'square' || report.pricing_units.labor === 'square') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium">Pricing Units</p>
              <p>Material rates: per {report.pricing_units.material === 'square' ? 'square (100 sqft)' : 'sq ft'}</p>
              <p>Labor rates: per {report.pricing_units.labor === 'square' ? 'square (100 sqft)' : 'sq ft'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailedReport;
